import {useCallback, useRef, useState} from 'react';

import {isNil} from 'ramda';
import {isNotNil} from 'ramda-adjunct';

/**
 * Return type for {@link useAvailableSizePx}.
 *
 * @property availableHeightPx - Remaining vertical space from the element's top
 *   edge to the bottom of its scroll viewport, measured in **whole CSS pixels**
 *   (value is floored).  `0` while the element has not mounted yet or when the
 *   element sits below the scroll viewport fold (no cap should be applied).
 * @property availableWidthPx - Remaining horizontal space from the element's
 *   left edge to the right edge of its **parent container**, measured in
 *   **whole CSS pixels** (value is floored).  `0` while the element has not
 *   mounted yet or has no parent.
 * @property callbackRef - A React **callback ref** to place on the element
 *   whose height and width you want to constrain.  Unlike a plain `useRef`, a
 *   callback ref fires as soon as the DOM node appears — even when it appears
 *   *after* an initial loading/skeleton early-return — so the size is always
 *   computed.
 */
type UseAvailableSizePxReturn = {
  availableHeightPx: number;
  availableWidthPx: number;
  callbackRef: (node: HTMLDivElement | null) => void;
};

export type UseAvailableSizePxOptions = {
  bottomInset?: number;
  /** Maximized grids fill a bounded flex parent instead of the page scroll viewport. */
  isBoundedByParent?: boolean;
};

/**
 * Measure the space available to an element in **raw CSS pixels** — both the
 * remaining height inside its scroll viewport below its top edge and the
 * remaining width to the right of its left edge within its parent container.
 *
 * ### Why this exists
 *
 * The platform's `useAvailableHeight` (from `platform/foundation`) returns
 * design-system *steps*, not pixels, and works through a standard `useRef` +
 * `useEffect` pattern.  That pattern breaks when the target element is rendered
 * *conditionally* — e.g. behind a loading guard that early-returns before the
 * element's JSX.  In that situation the ref is `null` when the effect runs, and
 * a `[]` dependency array means it never re-runs.
 *
 * Width has a parallel problem: when EagGrid is placed inside a flex-row layout
 * (e.g. a sidebar + grid arrangement using `HStack`), the platform `Box`
 * wrapper often receives `width: 100%` which resolves to the entire container
 * width instead of the remaining space after the sidebar.  This causes the grid
 * to overflow horizontally beyond the layout boundary.
 *
 * This hook solves both problems with a single callback ref / observer:
 *
 * 1. It returns **raw pixels** — suitable for setting inline `style.height` /
 *    `style.maxWidth` on a plain `<div>` that is not a platform `Box`.
 * 2. It uses a **callback ref** that fires exactly when the element mounts,
 *    regardless of how many renders preceded it with loading/error guards.
 *
 * ### How height is measured (scroll-invariant, T20-135515)
 *
 * The height cap exists so a *full-page* grid (top of the page, unbounded
 * parents) fits its scroll viewport without scrolling the page.  The naive
 * measurement `window.innerHeight − rect.top` is **scroll-dependent**: for a
 * grid embedded low on a scrollable page, the first measurement taken while
 * the user scrolls the grid into view (its top edge entering from the bottom
 * of the viewport) captures only the sliver between the grid top and the
 * viewport bottom, permanently squashing the grid to a few pixels
 * (interest → Selling vehicle list bug).
 *
 * The measurement is therefore taken against the element's **nearest
 * vertically scrollable ancestor** (e.g. the DMS `#dms-app-container` with
 * `overflow-y: auto`), using the element's offset *within that ancestor's
 * scrollable content* — a value that does not change as the user scrolls:
 *
 * ```
 * offsetInContent = element.rect.top − (ancestor.rect.top + ancestor.clientTop) + ancestor.scrollTop
 * availableHeight = ancestor.clientHeight − offsetInContent − bottomInset
 * ```
 *
 * (`clientTop` aligns the offset with the scrollport origin — `clientHeight`
 * excludes the ancestor's borders, so the offset must exclude them too.)
 *
 * - A full-page grid sits near the content top (`offsetInContent` small), so
 *   it gets the same viewport-fitting cap as before — regardless of the
 *   current scroll position when the measurement happens to run.
 * - An embedded grid far down a long page has `offsetInContent` larger than
 *   the ancestor's client height, the remaining space is negative → **no cap**
 *   is applied and `h-full` lets the bounded parent (e.g. a fixed-height Box)
 *   drive the grid height.
 *
 * When no scrollable ancestor exists the document itself is the scroll
 * context: `offsetInContent = rect.top + window.scrollY` against
 * `window.innerHeight` — identical to the previous behaviour for non-scrolled
 * pages, but equally scroll-invariant.
 *
 * Because the result no longer depends on the scroll position, no scroll
 * listener is needed at all — the previous scroll listener plus its
 * "stability lock" (ignore scroll after first valid measurement) was the
 * mechanism that locked in the sliver measurement in the first place.
 *
 * ### How width is measured (getBoundingClientRect, NOT offsetLeft)
 *
 * ```
 * relativeLeft = element.rect.left − parent.rect.left
 * availableWidth = parent.clientWidth − relativeLeft
 * ```
 *
 * `element.offsetLeft` is relative to the **offsetParent** (nearest positioned
 * ancestor), which can be far up the DOM tree — e.g. `<body>`.  When the grid
 * sits inside `<Box width="100%">` inside an `<HStack>`, `offsetLeft` includes
 * the entire left navigation sidebar + folder tree + separator, while
 * `parentElement.clientWidth` is only the Box's width — the mismatch produces a
 * far too narrow result.  `getBoundingClientRect().left` on both elements gives
 * the true visual offset relative to the viewport, so subtracting them yields
 * the correct offset within the parent.
 *
 * ### How it works
 *
 * A `ResizeObserver` on `document.body` recalculates both dimensions whenever
 * the page layout shifts (toolbar collapse, window resize, sidebar toggle,
 * etc.).  The element's **direct parent** is observed as well: when two of
 * these hooks nest (e.g. `EagGridScreenWrapper` around `EagGrid`), a window
 * grow first fires both callbacks while the outer wrapper still carries its
 * old inline `max-width`, so the inner measurement sees the stale parent
 * width.  The parent observation re-fires `calculate()` after the outer
 * wrapper re-renders and actually grows — without it the inner element stays
 * locked at the old width forever (monitor-resolution-change bug).
 *
 * To avoid unnecessary re-renders:
 *
 * - Each value is **floored** to whole pixels — subpixel jitter (e.g.
 *   `749.3 → 749.7`) does not trigger a state update.
 * - A **previous-value ref** per dimension skips `setState` when the floored
 *   result is identical to the last stored value.
 *
 * The only `useCallback` in this hook stabilises the callback ref so React
 * doesn't detach/reattach it on every render, which would disconnect and
 * recreate observers unnecessarily — this is required, not a premature
 * optimisation.
 *
 * ### Pure helpers
 *
 * The arithmetic is extracted into {@link calculateRemainingHeight} and
 * {@link calculateRemainingWidth} for unit testing without a DOM.
 *
 * @param options.bottomInset - Bottom inset in pixels to subtract from available height.
 *   Defaults to {@link BOTTOM_INSET_PX} (16px). Pass `0` for full-bleed layouts (e.g. mobile).
 *
 * @example
 * ```tsx
 * function MyGrid(props: MyGridProps) {
 *   const {availableHeightPx, availableWidthPx, callbackRef} = useAvailableSizePx();
 *
 *   if (props.isLoading) {
 *     return <Spinner />;           // callbackRef hasn't fired yet — fine
 *   }
 *
 *   return (
 *     <div
 *       ref={callbackRef}
 *       style={{
 *         height: availableHeightPx > 0 ? availableHeightPx : undefined,
 *         maxWidth: availableWidthPx > 0 ? availableWidthPx : undefined,
 *       }}
 *     >
 *       {props.children}
 *     </div>
 *   );
 * }
 * ```
 */
export function useAvailableSizePx(options?: UseAvailableSizePxOptions): UseAvailableSizePxReturn {
  const bottomInset = options?.bottomInset ?? BOTTOM_INSET_PX;
  const isBoundedByParent = options?.isBoundedByParent ?? false;
  const [availableHeightPx, setAvailableHeightPx] = useState<number>(0);
  const [availableWidthPx, setAvailableWidthPx] = useState<number>(0);
  const lastHeightRef = useRef<number>(0);
  const lastWidthRef = useRef<number>(0);
  const cleanupRef = useRef<(() => void) | null>(null);

  /**
   * @mandatory Callback ref passed to a DOM element via `ref={callbackRef}`.
   * React calls callback refs on every render if the reference changes.
   * Without useCallback, each render would detach and reattach the ref,
   * disconnecting and recreating ResizeObservers on every render — a severe
   * resource leak. bottomInset is the only real dependency.
   */
  const callbackRef = useCallback(
    (node: HTMLDivElement | null) => {
      cleanupRef.current?.();
      cleanupRef.current = null;

      if (!node) {
        return;
      }

      // Presentation shells use display:contents inline. They have no box to measure
      // or observe; use the nearest actual layout parent instead.
      let parent = node.parentElement;
      while (parent && getComputedStyle(parent).display === 'contents') {
        parent = parent.parentElement;
      }

      // The scroll ancestor of a mounted grid is structurally stable, but the
      // `scrollHeight > clientHeight` part of its detection is not: applying
      // the height cap can make the ancestor's content fit exactly, which
      // would flip the next detection to the window fallback and produce an
      // alternating measure→apply→re-measure loop. Caching the ancestor once
      // found keeps detection stable (and avoids re-walking the ancestor
      // chain with getComputedStyle on every ResizeObserver fire); the walk
      // is retried only while nothing has been found yet, so a container
      // that starts overflowing later is still picked up.
      let cachedScrollAncestor: HTMLElement | null = null;
      const resolveScrollAncestor = () => {
        if (isNil(cachedScrollAncestor) || !cachedScrollAncestor.isConnected) {
          cachedScrollAncestor = findScrollableAncestor(node);
        }
        return cachedScrollAncestor;
      };

      const calculate = () => {
        const nodeRect = node.getBoundingClientRect();

        if (isBoundedByParent && parent) {
          const height = parent.clientHeight;
          const width = parent.clientWidth;
          if (height !== lastHeightRef.current) {
            lastHeightRef.current = height;
            setAvailableHeightPx(height);
          }
          if (width !== lastWidthRef.current) {
            lastWidthRef.current = width;
            setAvailableWidthPx(width);
          }
          return;
        }

        // Scroll-invariant height: measure the element's offset within its
        // scroll container's CONTENT (not the window viewport) so the result
        // does not change as the user scrolls the page. See the doc block.
        // `clientTop` shifts the ancestor's border-box top to its scrollport
        // origin — `clientHeight` excludes borders, so the offset must too.
        const scrollAncestor = resolveScrollAncestor();
        const height = isNotNil(scrollAncestor)
          ? calculateRemainingHeight(
              nodeRect.top -
                (scrollAncestor.getBoundingClientRect().top + scrollAncestor.clientTop) +
                scrollAncestor.scrollTop,
              scrollAncestor.clientHeight,
              bottomInset
            )
          : calculateRemainingHeight(
              nodeRect.top + window.scrollY,
              window.innerHeight,
              bottomInset
            );

        // 0 is a meaningful result ("below the fold — apply no cap"), so it is
        // published like any other value: a layout shift that pushes the grid
        // below the fold must CLEAR a previously applied cap, otherwise a
        // transient early measurement stays locked in as a stale maxHeight
        // (the squashed-grid symptom of T20-135515 through another path).
        if (height !== lastHeightRef.current) {
          lastHeightRef.current = height;
          setAvailableHeightPx(height);
        }

        // Use getBoundingClientRect for both node and parent so the offset
        // is truly relative to the parent — not to the offsetParent which
        // can be an unrelated ancestor high up in the tree.
        if (parent) {
          const relativeLeft = nodeRect.left - parent.getBoundingClientRect().left;
          const width = calculateRemainingWidth(relativeLeft, parent.clientWidth);

          if (width > 0 && width !== lastWidthRef.current) {
            lastWidthRef.current = width;
            setAvailableWidthPx(width);
          }
        }
      };

      calculate();

      const observer = new ResizeObserver(calculate);
      observer.observe(document.body);
      // Also observe the direct parent: when nested width-capped wrappers react
      // to a window-size increase, the inner element measures its parent while
      // the parent still wears its OLD inline max-width (the outer wrapper's
      // React state has not re-rendered yet within the same observer frame).
      // Observing only document.body would never re-fire after the parent
      // subsequently grows, permanently locking the grid at the stale width
      // (grow-after-shrink / monitor-resolution-change bug). Observing the
      // parent re-runs calculate() once the parent's real size settles.
      if (parent) {
        observer.observe(parent);
      }

      cleanupRef.current = () => {
        observer.disconnect();
      };
    },
    [bottomInset, isBoundedByParent]
  );

  return {availableHeightPx, availableWidthPx, callbackRef};
}

/**
 * Bottom inset (px) subtracted from the available height so the grid card
 * does not sit flush against the viewport bottom.  The value mirrors the
 * outer layout padding visible in the reference AG Grid datagrid (16 px
 * gap between the card wrapper's bottom edge and the viewport).
 */
const BOTTOM_INSET_PX = 16;

/**
 * Overflow-y values that make an element a vertical scroll container.
 * `overlay` is a legacy WebKit value that behaves like `auto`.
 */
const SCROLLABLE_OVERFLOW_VALUES = ['auto', 'scroll', 'overlay'];

/**
 * Find the nearest ancestor that is an actual vertical scroll container:
 * computed `overflow-y` allows scrolling AND its content currently overflows
 * (`scrollHeight > clientHeight`).
 *
 * The overflow check alone is not enough — CSS computes `overflow-y` to
 * `auto` on elements that only declare `overflow-x`, so a purely horizontal
 * scroll wrapper would otherwise be mistaken for the vertical scroll context.
 * The `scrollHeight > clientHeight` guard skips those (and containers whose
 * content fits, where the window fallback yields the same result anyway).
 *
 * Exported for unit testing.  Not intended for direct use in components —
 * use {@link useAvailableSizePx} instead.
 */
export function findScrollableAncestor(element: HTMLElement): HTMLElement | null {
  let current = element.parentElement;
  while (isNotNil(current)) {
    const isScrollableOverflow = SCROLLABLE_OVERFLOW_VALUES.includes(
      getComputedStyle(current).overflowY
    );
    if (isScrollableOverflow && current.scrollHeight > current.clientHeight) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Pure helper: compute remaining scroll-viewport height from an element's
 * scroll-invariant top offset.
 *
 * `elementTop` is the element's offset within its scroll container's content
 * (`rect.top − ancestorRect.top + ancestor.scrollTop`, or
 * `rect.top + window.scrollY` for the document), and `viewportHeight` is the
 * scroll container's client height (or `window.innerHeight`).  Neither input
 * changes as the user scrolls, so the result is stable under scrolling.
 *
 * The result is **floored** to whole pixels so that subpixel layout jitter does
 * not cause unnecessary React state updates.  A configurable {@link bottomInset}
 * is subtracted so the grid card mirrors the reference datagrid's bottom gap.
 *
 * Exported for unit testing.  Not intended for direct use in components — use
 * {@link useAvailableSizePx} instead.
 *
 * @param elementTop - Scroll-invariant offset in pixels from the scroll
 *   viewport's content top to the element's top border edge.
 * @param viewportHeight - The scroll container's `clientHeight` (or
 *   `window.innerHeight`) at measurement time.
 * @param bottomInset - Bottom inset in pixels (defaults to {@link BOTTOM_INSET_PX}).
 * @returns Whole CSS pixels remaining below the element's top edge.
 *   Always `>= 0`; returns `0` when the element is at or below the scroll
 *   viewport fold (meaning: apply no height cap).
 */
export function calculateRemainingHeight(
  elementTop: number,
  viewportHeight: number,
  bottomInset: number = BOTTOM_INSET_PX
): number {
  const remaining = viewportHeight - elementTop - bottomInset;
  return remaining > 0 ? Math.floor(remaining) : 0;
}

/**
 * Pure helper: compute remaining parent width from an element's relative left
 * offset within its parent.
 *
 * ```
 * remaining = parentClientWidth − relativeLeftInParent
 * ```
 *
 * The result is **floored** to whole pixels so that subpixel layout jitter
 * does not cause unnecessary React state updates.
 *
 * Exported for unit testing.  Not intended for direct use in components —
 * use {@link useAvailableSizePx} instead.
 *
 * @param relativeLeftInParent - Distance in pixels from the element's left
 *   edge to its parent's left edge, computed via
 *   `node.getBoundingClientRect().left − parent.getBoundingClientRect().left`.
 * @param parentClientWidth - `element.parentElement.clientWidth` at
 *   measurement time.
 * @returns Whole CSS pixels remaining to the right of the element's left
 *   edge within its parent.  Always `>= 0`; returns `0` when
 *   `relativeLeftInParent >= parentClientWidth`.
 */
export function calculateRemainingWidth(
  relativeLeftInParent: number,
  parentClientWidth: number
): number {
  const remaining = parentClientWidth - relativeLeftInParent;
  return remaining > 0 ? Math.floor(remaining) : 0;
}
